import React, { Fragment, useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";

const baseBtnStyle = {
  margin: "5px",
  display: "flex",
  justifyContent: "center",
  fontWeight: "bold",
};

const gradients = {
  answeredFlagged: "linear-gradient(to right, #007bff 50% , #ffc107 50%)",
  halfAnsweredFlagged:
    "linear-gradient(to right, #007bff 0, #007bff 33%, #6c757d 33%, #6c757d 66%, #ffc107 66%, #ffc107 100%)",
  halfAnswered: "linear-gradient(to right, #007bff 50% , #6c757d 50%)",
  flagged: "linear-gradient(to right, #6c757d 50%, #ffc107 50%)",
};

function getVariantAndStyle(status, { answeredVariant = "primary" } = {}) {
  const answered = !!status?.answered;
  const flagged = !!status?.flagged;
  const halfanswered = !!status?.halfanswered;
  const visited = !!status?.visited;

  if (answered && flagged) return { as: Button, variant: undefined, style: { backgroundImage: gradients.answeredFlagged } };
  if (answered) return { as: Button, variant: answeredVariant, style: {} };
  if (flagged && halfanswered) return { as: "button", variant: undefined, style: { backgroundImage: gradients.halfAnsweredFlagged } };
  if (halfanswered) return { as: "button", variant: undefined, style: { backgroundImage: gradients.halfAnswered } };
  if (flagged) return { as: Button, variant: undefined, style: { backgroundImage: gradients.flagged } };
  if (visited) return { as: Button, variant: "secondary", style: {} };
  return { as: Button, variant: "dark", style: {} };
}

function rangeLabel(section) {
  const first = section?.sections?.[0]?.qIndex;
  const last = section?.sections?.[section?.sections?.length - 1]?.qIndex;
  return `Q${first}-Q${last}`;
}

function twoDigit(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * Toggle flagged status and keep aggregate flags in sync.
 * NOTE: This preserves your current “mutate then setState clone” behavior.
 */
function toggleFlag(SectionsStatus, sectionIndex, subIndex, qusID) {
  const q = SectionsStatus?.[sectionIndex]?.[subIndex]?.[qusID];
  if (!q) return;

  q.flagged = !q.flagged;

  // If flagged => bubble up
  if (q.flagged) {
    SectionsStatus[sectionIndex][subIndex].flagged = true;
    SectionsStatus[sectionIndex].flagged = true;
    return;
  }

  // Recompute subsection flagged
  let subHasFlag = false;
  for (const key in SectionsStatus[sectionIndex][subIndex]) {
    const node = SectionsStatus[sectionIndex][subIndex][key];
    if (typeof node === "object" && node?.flagged) {
      subHasFlag = true;
      break;
    }
  }
  SectionsStatus[sectionIndex][subIndex].flagged = subHasFlag;

  // Recompute section flagged
  let secHasFlag = false;
  for (const sKey in SectionsStatus[sectionIndex]) {
    const subNode = SectionsStatus[sectionIndex][sKey];
    if (typeof subNode === "object") {
      for (const qKey in subNode) {
        const qNode = subNode[qKey];
        if (typeof qNode === "object" && qNode?.flagged) {
          secHasFlag = true;
          break;
        }
      }
    }
    if (secHasFlag) break;
  }
  SectionsStatus[sectionIndex].flagged = secHasFlag;
}

function StatusButton({
  label,
  status,
  onClick,
  size = "10px",
  fullWidth = false,
  highlighted = false,
  answeredVariant = "primary",
}) {
  const { as, variant, style } = getVariantAndStyle(status, { answeredVariant });

  const commonStyle = {
    ...baseBtnStyle,
    fontSize: size,
    ...(fullWidth ? { width: "100%", padding: "5px" } : {}),
    ...(highlighted ? { border: "2px solid black" } : {}),
    ...style,
  };

  const Component = as;

  // For plain <button>, mimic bootstrap "btn"
  if (Component === "button") {
    return (
      <button className="btn" style={commonStyle} onClick={onClick}>
        {label}
      </button>
    );
  }

  return (
    <Button style={commonStyle} variant={variant} onClick={onClick}>
      {label}
    </Button>
  );
}

function SubQuestionIndicators({ el, answersFromStore }) {
  const show =
    el?.qusType === "Match the following" || el?.qusType !== "MCQ";
  if (!show) return null;

  let count = 0;

  if (el?.qusType === "Match the following") {
    try {
      const parsed = JSON.parse(el.correctOption);
      count = parsed?.[0] ? Object.keys(parsed[0]).length : 0;
    } catch {
      count = 0;
    }
  } else {
    try {
      const parsed = JSON.parse(el.correctOption);
      count = Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      count = 0;
    }
  }

  if (!count) return null;

  const stored = answersFromStore?.[Number(el.qusID)];
  const selected = stored?.selectedAnswer || [];

  return (
    <div className="d-flex flex-row flex-wrap">
      {Array.from({ length: count }).map((_, idx) => {
        const hasValue =
          selected[idx] !== undefined &&
          selected[idx] !== "" &&
          selected[idx] !== "Select Answer";

        return (
          <Button
            key={`${hasValue ? "true" : "false"}${el.qusID}${idx}`}
            style={{ margin: "2px", fontSize: "11px", fontWeight: "bold", padding: "5px" }}
            variant={hasValue ? "primary" : "secondary"}
          >
            {idx + 1}
          </Button>
        );
      })}
    </div>
  );
}

export default function QuestionPallet(props) {
  const {
    Sections,
    SectionsStatus,
    QuestionToggle,
    currentSectionId,
    currentSubSectionSingleId,
    sectionIdChange,
    setQuestionToggle,
    setcurrentSubSectionSingleId,
    subSectionIdChange,
    setSectionsStatus,
    answersFromStore,
    submitClicked,
  } = props;

  const onTogglePalette = useCallback(() => {
    setQuestionToggle((p) => !p);
  }, [setQuestionToggle]);

  const safeSections = Sections ?? [];

  const renderSectionHeader = (section, index, { clickableWhenClosed }) => {
    const label = rangeLabel(section);

    // Closed state on current section: show a “light” bordered button to open
    if (clickableWhenClosed) {
      return (
        <StatusButton
          label={label}
          status={null}
          size="10px"
          highlighted
          onClick={onTogglePalette}
        />
      );
    }

    const status = SectionsStatus?.[index];
    return (
      <StatusButton
        label={label}
        status={status}
        size="10px"
        onClick={() => sectionIdChange(index)}
      />
    );
  };

  const renderQuestionButton = (sectionIndex, subsection, subsectionind, el, ind) => {
    const qStatus = SectionsStatus?.[sectionIndex]?.[subsectionind]?.[el.qusID];
    const label = twoDigit(subsection.qIndex + ind);

    const isCurrent =
      currentSubSectionSingleId === subsectionind && subsection.section === 0;

    return (
      <StatusButton
        label={label}
        status={isCurrent ? null : qStatus}
        size="9px"
        fullWidth
        highlighted={isCurrent}
        answeredVariant="primary"
        onClick={() => {
          setcurrentSubSectionSingleId(subsectionind);
          subSectionIdChange(el.qusID, sectionIndex);
        }}
      />
    );
  };

  const renderFlagButton = (sectionIndex, subsectionind, el) => {
    const flagged = !!SectionsStatus?.[sectionIndex]?.[subsectionind]?.[el.qusID]?.flagged;

    return (
      <Button
        style={{
          ...baseBtnStyle,
          width: "100%",
          margin: "5px",
          fontSize: submitClicked ? "12px" : "11px",
          padding: "5px",
        }}
        variant="warning"
        onClick={() => {
          toggleFlag(SectionsStatus, sectionIndex, subsectionind, el.qusID);
          setSectionsStatus({ ...SectionsStatus });
        }}
      >
        <i
          style={{ color: flagged ? "red" : "white" }}
          className="fa fa-flag"
        />
      </Button>
    );
  };

  const renderExpandedSection = (sectionIndex) => {
    const section = safeSections?.[sectionIndex];
    if (!section) return null;

    return (
      <>
        {!submitClicked && (
          <div style={{ background: "lightblue", width: "100%" }}>
            <Button
              style={{
                ...baseBtnStyle,
                margin: "5px",
                border: "2px solid black",
                fontSize: "12px",
              }}
              variant="light"
              onClick={onTogglePalette}
            >
              {rangeLabel(section)}
            </Button>
          </div>
        )}

        <div
          style={{
            background: "lightblue",
            width: "100%",
            marginBottom: submitClicked ? "5px" : undefined,
          }}
          className="d-flex flex-row flex-wrap"
        >
          {section.sections?.map((subsection, subsectionind) => (
            <div key={`${sectionIndex}-${subsectionind}`} style={{ padding: "10px" }}>
              <div className="d-flex flex-row flex-wrap">
                {subsection.questions?.map((el, ind) => (
                  <div key={`${el.qusID}-${ind}`} style={{ padding: "10px" }}>
                    <div className="d-flex flex-row flex-wrap">
                      <div className="">
                        {renderQuestionButton(sectionIndex, subsection, subsectionind, el, ind)}
                      </div>

                      <div className="">{renderFlagButton(sectionIndex, subsectionind, el)}</div>
                    </div>

                    <SubQuestionIndicators el={el} answersFromStore={answersFromStore} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div>
      {!submitClicked ? (
        <div className="">
          <div className="d-flex flex-row flex-wrap">
            {safeSections.map((section, index) => {
              const isCurrent = index === currentSectionId;
              const isOpen = isCurrent && QuestionToggle;

              return (
                <Fragment key={index}>
                  <span style={{ background: isOpen ? "lightblue" : "" }}>
                    {isCurrent
                      ? !QuestionToggle && renderSectionHeader(section, index, { clickableWhenClosed: true })
                      : renderSectionHeader(section, index, { clickableWhenClosed: false })}
                  </span>

                  {isOpen && renderExpandedSection(currentSectionId)}
                </Fragment>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="">
          <div className="d-flex flex-row flex-wrap">
            {safeSections.map((section, index) => (
              <Fragment key={index}>
                <span style={{ background: "lightblue" }}>
                  {renderSectionHeader(section, index, { clickableWhenClosed: false })}
                </span>

                {renderExpandedSection(index)}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}